// Cuestionarios Clínicos Adicionales
// ODI (Oswestry Disability Index) y EFP-0 (Estado Funcional y Propioceptivo)

import { QUESTION_TYPES } from './questionnaireSchemas';

// ==================== ODI - Oswestry Disability Index ====================
// Cuestionario estándar para evaluar discapacidad por dolor lumbar
export const ODI_SCHEMA = {
  id: 'odi',
  name: 'ODI',
  fullName: 'Índice de Discapacidad de Oswestry',
  description: 'Cuestionario de 10 secciones para evaluar el impacto del dolor lumbar en las actividades diarias.',
  estimatedTime: '5-7 minutos',
  category: 'dolor_lumbar',
  version: '2.1a',
  clinicalCode: 'ODI-2.1a',
  scoring: {
    type: 'percentage',
    method: 'oswestry',
    maxScore: 50,
    interpretation: [
      { min: 0, max: 20, level: 'minimal', label: 'Discapacidad mínima', color: 'green' },
      { min: 21, max: 40, level: 'moderate', label: 'Discapacidad moderada', color: 'yellow' },
      { min: 41, max: 60, level: 'severe', label: 'Discapacidad severa', color: 'orange' },
      { min: 61, max: 80, level: 'crippling', label: 'Discapacidad muy severa', color: 'red' },
      { min: 81, max: 100, level: 'bedbound', label: 'Postrado/Exageración', color: 'purple' },
    ],
  },
  sections: [
    {
      id: 'intensidad_dolor',
      title: 'Sección 1: Intensidad del Dolor',
      questions: [
        {
          id: 'odi_1',
          text: 'Intensidad del dolor',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'Puedo soportar el dolor sin necesidad de tomar calmantes' },
            { value: 1, label: 'El dolor es fuerte pero me arreglo sin tomar calmantes' },
            { value: 2, label: 'Los calmantes me alivian completamente el dolor' },
            { value: 3, label: 'Los calmantes me alivian un poco el dolor' },
            { value: 4, label: 'Los calmantes apenas me alivian el dolor' },
            { value: 5, label: 'Los calmantes no me quitan el dolor y no los tomo' },
          ],
        },
      ],
    },
    {
      id: 'cuidados_personales',
      title: 'Sección 2: Cuidados Personales (lavarse, vestirse, etc.)',
      questions: [
        {
          id: 'odi_2',
          text: 'Cuidados personales',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'Me las puedo arreglar solo sin que me aumente el dolor' },
            { value: 1, label: 'Me las puedo arreglar solo pero esto me aumenta el dolor' },
            { value: 2, label: 'Lavarme, vestirme, etc. me produce dolor y tengo que hacerlo despacio y con cuidado' },
            { value: 3, label: 'Necesito alguna ayuda pero consigo hacer la mayoría de las cosas yo solo' },
            { value: 4, label: 'Necesito ayuda para hacer la mayoría de las cosas' },
            { value: 5, label: 'No puedo vestirme, me cuesta lavarme y suelo quedarme en la cama' },
          ],
        },
      ],
    },
    {
      id: 'levantar_peso',
      title: 'Sección 3: Levantar Peso',
      questions: [
        {
          id: 'odi_3',
          text: 'Levantar peso',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'Puedo levantar objetos pesados sin que me aumente el dolor' },
            { value: 1, label: 'Puedo levantar objetos pesados pero me aumenta el dolor' },
            { value: 2, label: 'El dolor me impide levantar objetos pesados del suelo, pero puedo hacerlo si están en un sitio cómodo (ej: en una mesa)' },
            { value: 3, label: 'El dolor me impide levantar objetos pesados, pero sí puedo levantar objetos ligeros o medianos si están en un sitio cómodo' },
            { value: 4, label: 'Sólo puedo levantar objetos muy ligeros' },
            { value: 5, label: 'No puedo levantar ni elevar ningún objeto' },
          ],
        },
      ],
    },
    {
      id: 'andar',
      title: 'Sección 4: Andar',
      questions: [
        {
          id: 'odi_4',
          text: 'Andar',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'El dolor no me impide andar cualquier distancia' },
            { value: 1, label: 'El dolor me impide andar más de un kilómetro' },
            { value: 2, label: 'El dolor me impide andar más de 500 metros' },
            { value: 3, label: 'El dolor me impide andar más de 250 metros' },
            { value: 4, label: 'Sólo puedo andar con bastón o muletas' },
            { value: 5, label: 'Permanezco en la cama casi todo el tiempo y tengo que ir a rastras al baño' },
          ],
        },
      ],
    },
    {
      id: 'estar_sentado',
      title: 'Sección 5: Estar Sentado',
      questions: [
        {
          id: 'odi_5',
          text: 'Estar sentado',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'Puedo estar sentado en cualquier tipo de silla todo el tiempo que quiera' },
            { value: 1, label: 'Solo puedo estar sentado en mi silla favorita todo el tiempo que quiera' },
            { value: 2, label: 'El dolor me impide estar sentado más de una hora' },
            { value: 3, label: 'El dolor me impide estar sentado más de media hora' },
            { value: 4, label: 'El dolor me impide estar sentado más de 10 minutos' },
            { value: 5, label: 'El dolor me impide estar sentado' },
          ],
        },
      ],
    },
    {
      id: 'estar_de_pie',
      title: 'Sección 6: Estar de Pie',
      questions: [
        {
          id: 'odi_6',
          text: 'Estar de pie',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'Puedo estar de pie todo el tiempo que quiera sin que me aumente el dolor' },
            { value: 1, label: 'Puedo estar de pie todo el tiempo que quiera pero me aumenta el dolor' },
            { value: 2, label: 'El dolor me impide estar de pie más de una hora' },
            { value: 3, label: 'El dolor me impide estar de pie más de media hora' },
            { value: 4, label: 'El dolor me impide estar de pie más de 10 minutos' },
            { value: 5, label: 'El dolor me impide estar de pie' },
          ],
        },
      ],
    },
    {
      id: 'dormir',
      title: 'Sección 7: Dormir',
      questions: [
        {
          id: 'odi_7',
          text: 'Dormir',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'El dolor no me impide dormir bien' },
            { value: 1, label: 'Sólo puedo dormir si tomo pastillas' },
            { value: 2, label: 'Incluso tomando pastillas duermo menos de 6 horas' },
            { value: 3, label: 'Incluso tomando pastillas duermo menos de 4 horas' },
            { value: 4, label: 'Incluso tomando pastillas duermo menos de 2 horas' },
            { value: 5, label: 'El dolor me impide totalmente dormir' },
          ],
        },
      ],
    },
    {
      id: 'actividad_sexual',
      title: 'Sección 8: Actividad Sexual (si es aplicable)',
      questions: [
        {
          id: 'odi_8',
          text: 'Actividad sexual',
          type: QUESTION_TYPES.RADIO,
          required: false,
          options: [
            { value: 0, label: 'Mi actividad sexual es normal y no me aumenta el dolor' },
            { value: 1, label: 'Mi actividad sexual es normal pero me aumenta el dolor' },
            { value: 2, label: 'Mi actividad sexual es casi normal pero me aumenta mucho el dolor' },
            { value: 3, label: 'Mi actividad sexual se ha visto muy limitada a causa del dolor' },
            { value: 4, label: 'Mi actividad sexual es casi nula a causa del dolor' },
            { value: 5, label: 'El dolor me impide todo tipo de actividad sexual' },
          ],
        },
      ],
    },
    {
      id: 'vida_social',
      title: 'Sección 9: Vida Social',
      questions: [
        {
          id: 'odi_9',
          text: 'Vida social',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'Mi vida social es normal y no me aumenta el dolor' },
            { value: 1, label: 'Mi vida social es normal pero me aumenta el dolor' },
            { value: 2, label: 'El dolor no tiene un efecto importante en mi vida social, pero sí impide mis actividades más enérgicas como bailar, etc.' },
            { value: 3, label: 'El dolor ha limitado mi vida social y no salgo tan a menudo' },
            { value: 4, label: 'El dolor ha limitado mi vida social al hogar' },
            { value: 5, label: 'No tengo vida social a causa del dolor' },
          ],
        },
      ],
    },
    {
      id: 'viajar',
      title: 'Sección 10: Viajar',
      questions: [
        {
          id: 'odi_10',
          text: 'Viajar',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 0, label: 'Puedo viajar a cualquier sitio sin que me aumente el dolor' },
            { value: 1, label: 'Puedo viajar a cualquier sitio pero me aumenta el dolor' },
            { value: 2, label: 'El dolor es fuerte pero aguanto viajes de más de 2 horas' },
            { value: 3, label: 'El dolor me limita a viajes de menos de una hora' },
            { value: 4, label: 'El dolor me limita a viajes cortos y necesarios de menos de media hora' },
            { value: 5, label: 'El dolor me impide viajar excepto para ir al médico o al hospital' },
          ],
        },
      ],
    },
  ],
};

// ==================== EFP-0 - Estado Funcional y Propioceptivo ====================
// Evaluación del estado funcional, equilibrio y propiocepción
export const EFP0_SCHEMA = {
  id: 'efp0',
  name: 'EFP-0',
  fullName: 'Evaluación Funcional y Propioceptiva Inicial',
  description: 'Cuestionario para evaluar el estado funcional, equilibrio, coordinación y propiocepción del paciente.',
  estimatedTime: '4-6 minutos',
  category: 'funcional',
  version: '1.0',
  clinicalCode: 'EFP-0',
  scoring: {
    type: 'domains',
    method: 'weighted_average',
    domains: ['equilibrio', 'coordinacion', 'fuerza', 'flexibilidad', 'propiocepcion'],
  },
  sections: [
    {
      id: 'equilibrio_estatico',
      title: 'Equilibrio Estático',
      description: 'Evalúe su capacidad de mantener el equilibrio en diferentes posiciones.',
      domain: 'equilibrio',
      questions: [
        {
          id: 'efp_eq1',
          text: '¿Puede mantenerse de pie con los pies juntos durante 30 segundos sin apoyo?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, sin ninguna dificultad' },
            { value: 3, label: 'Sí, con ligera inestabilidad' },
            { value: 2, label: 'Sí, pero necesito concentrarme mucho' },
            { value: 1, label: 'Necesito apoyo ocasional' },
            { value: 0, label: 'No puedo sin apoyo continuo' },
          ],
          scoring: { domain: 'equilibrio', weight: 1 },
        },
        {
          id: 'efp_eq2',
          text: '¿Puede mantenerse sobre una sola pierna durante 10 segundos?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, ambas piernas sin problema' },
            { value: 3, label: 'Sí, pero una pierna es más inestable' },
            { value: 2, label: 'Solo puedo con una pierna' },
            { value: 1, label: 'Menos de 5 segundos en ambas' },
            { value: 0, label: 'No puedo mantenerme en una pierna' },
          ],
          scoring: { domain: 'equilibrio', weight: 1.5 },
        },
      ],
    },
    {
      id: 'equilibrio_dinamico',
      title: 'Equilibrio Dinámico',
      description: 'Evalúe su equilibrio durante el movimiento.',
      domain: 'equilibrio',
      questions: [
        {
          id: 'efp_eq3',
          text: '¿Puede caminar en línea recta (talón-punta) durante 3 metros?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, sin dificultad' },
            { value: 3, label: 'Sí, con algún paso fuera de línea' },
            { value: 2, label: 'Completo pero con dificultad evidente' },
            { value: 1, label: 'Solo parcialmente' },
            { value: 0, label: 'No puedo hacerlo' },
          ],
          scoring: { domain: 'equilibrio', weight: 1.5 },
        },
        {
          id: 'efp_eq4',
          text: '¿Siente mareos o inestabilidad al girar la cabeza mientras camina?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Nunca' },
            { value: 3, label: 'Raramente' },
            { value: 2, label: 'A veces' },
            { value: 1, label: 'Frecuentemente' },
            { value: 0, label: 'Siempre' },
          ],
          scoring: { domain: 'equilibrio', weight: 1, invert: false },
        },
      ],
    },
    {
      id: 'coordinacion',
      title: 'Coordinación',
      description: 'Evalúe su capacidad de coordinar movimientos.',
      domain: 'coordinacion',
      questions: [
        {
          id: 'efp_co1',
          text: '¿Puede tocarse la nariz con el dedo índice alternando manos con los ojos cerrados?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, con precisión' },
            { value: 3, label: 'Sí, con ligera desviación' },
            { value: 2, label: 'Con dificultad moderada' },
            { value: 1, label: 'Con mucha dificultad' },
            { value: 0, label: 'No puedo hacerlo' },
          ],
          scoring: { domain: 'coordinacion', weight: 1 },
        },
        {
          id: 'efp_co2',
          text: '¿Tiene dificultad para realizar movimientos finos (abrochar botones, escribir)?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Ninguna dificultad' },
            { value: 3, label: 'Dificultad mínima' },
            { value: 2, label: 'Dificultad moderada' },
            { value: 1, label: 'Dificultad significativa' },
            { value: 0, label: 'Imposible sin ayuda' },
          ],
          scoring: { domain: 'coordinacion', weight: 1 },
        },
      ],
    },
    {
      id: 'fuerza_funcional',
      title: 'Fuerza Funcional',
      description: 'Evalúe su capacidad de realizar esfuerzos.',
      domain: 'fuerza',
      questions: [
        {
          id: 'efp_fu1',
          text: '¿Puede levantarse de una silla sin usar las manos?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, fácilmente' },
            { value: 3, label: 'Sí, con algo de esfuerzo' },
            { value: 2, label: 'Necesito impulso o varios intentos' },
            { value: 1, label: 'Necesito apoyo de las manos' },
            { value: 0, label: 'Necesito ayuda de otra persona' },
          ],
          scoring: { domain: 'fuerza', weight: 1.5 },
        },
        {
          id: 'efp_fu2',
          text: '¿Puede subir un tramo de escaleras sin barandilla?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, sin dificultad' },
            { value: 3, label: 'Sí, pero prefiero la barandilla' },
            { value: 2, label: 'Necesito la barandilla' },
            { value: 1, label: 'Subo con mucha dificultad' },
            { value: 0, label: 'No puedo subir escaleras' },
          ],
          scoring: { domain: 'fuerza', weight: 1 },
        },
      ],
    },
    {
      id: 'flexibilidad',
      title: 'Flexibilidad',
      description: 'Evalúe su rango de movimiento.',
      domain: 'flexibilidad',
      questions: [
        {
          id: 'efp_fl1',
          text: '¿Puede tocarse los dedos de los pies con las rodillas estiradas?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, toco el suelo' },
            { value: 3, label: 'Toco los dedos de los pies' },
            { value: 2, label: 'Llego a los tobillos' },
            { value: 1, label: 'Llego a media pantorrilla' },
            { value: 0, label: 'No llego más allá de las rodillas' },
          ],
          scoring: { domain: 'flexibilidad', weight: 1 },
        },
        {
          id: 'efp_fl2',
          text: '¿Puede girar el cuello completamente hacia ambos lados?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, rango completo sin molestias' },
            { value: 3, label: 'Rango completo con ligeras molestias' },
            { value: 2, label: 'Rango limitado en un lado' },
            { value: 1, label: 'Rango muy limitado en ambos lados' },
            { value: 0, label: 'Movimiento muy restringido' },
          ],
          scoring: { domain: 'flexibilidad', weight: 1 },
        },
      ],
    },
    {
      id: 'propiocepcion',
      title: 'Propiocepción',
      description: 'Evalúe su percepción de la posición corporal.',
      domain: 'propiocepcion',
      questions: [
        {
          id: 'efp_pr1',
          text: '¿Puede mantener el equilibrio con los ojos cerrados durante 20 segundos?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Sí, sin problema' },
            { value: 3, label: 'Sí, con ligero balanceo' },
            { value: 2, label: 'Solo 10-15 segundos' },
            { value: 1, label: 'Menos de 10 segundos' },
            { value: 0, label: 'Pierdo el equilibrio inmediatamente' },
          ],
          scoring: { domain: 'propiocepcion', weight: 1.5 },
        },
        {
          id: 'efp_pr2',
          text: '¿Tiene dificultad para caminar en superficies irregulares (arena, gravilla)?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Ninguna dificultad' },
            { value: 3, label: 'Dificultad mínima' },
            { value: 2, label: 'Necesito más atención' },
            { value: 1, label: 'Evito estas superficies' },
            { value: 0, label: 'No puedo caminar en ellas' },
          ],
          scoring: { domain: 'propiocepcion', weight: 1 },
        },
        {
          id: 'efp_pr3',
          text: '¿Ha tenido caídas o tropiezos en los últimos 3 meses?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 4, label: 'Ninguno' },
            { value: 3, label: '1 tropiezo sin caída' },
            { value: 2, label: '2-3 tropiezos' },
            { value: 1, label: '1 caída' },
            { value: 0, label: 'Múltiples caídas' },
          ],
          scoring: { domain: 'propiocepcion', weight: 2, redFlag: true },
        },
      ],
    },
    {
      id: 'observaciones',
      title: 'Observaciones Adicionales',
      questions: [
        {
          id: 'efp_obs',
          text: '¿Hay alguna otra limitación funcional que quiera comentar?',
          type: QUESTION_TYPES.TEXTAREA,
          required: false,
          placeholder: 'Describa cualquier otra dificultad funcional que experimente...',
          maxLength: 500,
        },
      ],
    },
  ],
};

// ==================== FUNCIONES DE CÁLCULO DE SCORING ====================

// Calcular score ODI (Oswestry Disability Index)
export const calculateODIScore = (answers) => {
  const questions = ['odi_1', 'odi_2', 'odi_3', 'odi_4', 'odi_5', 'odi_6', 'odi_7', 'odi_8', 'odi_9', 'odi_10'];
  let totalScore = 0;
  let answeredSections = 0;

  questions.forEach(qId => {
    const answer = answers[qId];
    if (answer !== undefined && answer !== null) {
      totalScore += answer;
      answeredSections++;
    }
  });

  // El máximo se ajusta a lo CONTESTADO, que es el método oficial del ODI:
  // una sección omitida no se cuenta ni arriba ni abajo.
  const maxPossible = answeredSections * 5;

  /* 🔴 DOS AGUJEROS, ARREGLADOS (Ann, 28-ago-2026 · «que las puntuaciones se
     hagan bien y correctas»).

     1) SIN NINGUNA RESPUESTA DEVOLVÍA 0 %. Y en esta escala el 0 % no es
        «no hay dato»: es «no te limita NADA», el mejor resultado posible.
        Un ODI vacío salía como una espalda perfecta.

     2) NO HABÍA MÍNIMO. Con dos secciones contestadas ya daba un porcentaje
        con pinta de índice válido. El ODI se calcula sobre lo respondido,
        pero eso vale para UNA sección omitida, no para ocho.

     El umbral (8 de 10) es el que Ann ya tenía escrito en el prototipo de
     FisioVisión: «con menos de 8, no hay resultado». Se usa el suyo. */
  if (answeredSections < 8) {
    return {
      raw: null, percentage: null, maxPossible,
      answeredSections, incompleto: true,
      interpretation: {
        level: 'incompleto',
        label: `Sin resultado · solo ${answeredSections} de 10 secciones`,
        color: 'gray',
      },
    };
  }

  const percentage = Math.round((totalScore / maxPossible) * 100);

  // Determinar nivel de discapacidad
  let interpretation = ODI_SCHEMA.scoring.interpretation.find(
    i => percentage >= i.min && percentage <= i.max
  ) || { level: 'unknown', label: 'No determinado', color: 'gray' };

  return {
    raw: totalScore,
    percentage,
    maxPossible,
    answeredSections,
    interpretation: interpretation.label,
    level: interpretation.level,
    color: interpretation.color,
    redFlags: percentage > 60 ? ['Discapacidad severa detectada - Requiere evaluación especializada'] : [],
  };
};

// Calcular score EFP-0 (Estado Funcional y Propioceptivo)
export const calculateEFP0Score = (answers) => {
  const domains = {
    equilibrio: { total: 0, weight: 0, items: 0 },
    coordinacion: { total: 0, weight: 0, items: 0 },
    fuerza: { total: 0, weight: 0, items: 0 },
    flexibilidad: { total: 0, weight: 0, items: 0 },
    propiocepcion: { total: 0, weight: 0, items: 0 },
  };

  const redFlags = [];

  // Procesar todas las secciones
  EFP0_SCHEMA.sections.forEach(section => {
    if (!section.domain) return;
    
    section.questions.forEach(question => {
      if (question.type === QUESTION_TYPES.TEXTAREA) return;
      
      const answer = answers[question.id];
      if (answer !== undefined && answer !== null) {
        const weight = question.scoring?.weight || 1;
        const domain = question.scoring?.domain || section.domain;
        
        if (domains[domain]) {
          domains[domain].total += answer * weight;
          domains[domain].weight += weight * 4; // Max value is 4
          domains[domain].items++;
        }

        // Detectar banderas rojas
        if (question.scoring?.redFlag && answer <= 1) {
          redFlags.push(`Atención: ${question.text} - Puntuación baja detectada`);
        }
      }
    });
  });

  // Calcular porcentajes por dominio
  const domainScores = {};
  let totalWeightedScore = 0;
  let totalWeight = 0;

  Object.entries(domains).forEach(([domain, data]) => {
    if (data.weight > 0) {
      const percentage = Math.round((data.total / data.weight) * 100);
      domainScores[domain] = {
        score: percentage,
        label: getDomainLabel(domain),
        status: getStatusFromScore(percentage),
      };
      totalWeightedScore += percentage * data.items;
      totalWeight += data.items;
    }
  });

  /* 🔴 SIN RESPUESTAS NO HAY PUNTUACIÓN (Ann, 28-ago-2026).
     Antes devolvía 0. Y aquí el 0 no es «no hay dato»: en esta escala más es
     mejor, así que 0 es EL PEOR ESTADO FUNCIONAL POSIBLE. Un EFP-0 sin
     contestar salía como una alarma, y encima disparaba las banderas rojas.
     Es el mismo fallo que tenía el ODI, pero al revés.

     ⚠️ FALTA DECIDIR UN MÍNIMO. Hoy, con UNA sola pregunta contestada, ya
     devuelve un porcentaje global. En el ODI el umbral (8 de 10) es el
     estándar de la escala; el EFP-0 es instrumento propio del Método, así que
     el mínimo lo fija Ann, no yo. Queda anotado. */
  if (totalWeight <= 0) {
    return {
      global: null, domains: domainScores, incompleto: true,
      interpretation: 'Sin respuestas · no hay puntuación',
      redFlags: [], recommendations: [],
    };
  }
  const globalScore = Math.round(totalWeightedScore / totalWeight);

  return {
    global: globalScore,
    domains: domainScores,
    interpretation: getGlobalInterpretation(globalScore),
    redFlags,
    recommendations: generateRecommendations(domainScores),
  };
};

// Funciones auxiliares
const getDomainLabel = (domain) => {
  const labels = {
    equilibrio: 'Equilibrio',
    coordinacion: 'Coordinación',
    fuerza: 'Fuerza Funcional',
    flexibilidad: 'Flexibilidad',
    propiocepcion: 'Propiocepción',
  };
  return labels[domain] || domain;
};

const getStatusFromScore = (score) => {
  if (score >= 80) return { label: 'Óptimo', color: 'green' };
  if (score >= 60) return { label: 'Bueno', color: 'blue' };
  if (score >= 40) return { label: 'Moderado', color: 'yellow' };
  if (score >= 20) return { label: 'Deficiente', color: 'orange' };
  return { label: 'Muy deficiente', color: 'red' };
};

const getGlobalInterpretation = (score) => {
  if (score >= 80) return 'Estado funcional óptimo';
  if (score >= 60) return 'Estado funcional bueno con áreas de mejora';
  if (score >= 40) return 'Estado funcional moderado - Se recomienda intervención';
  if (score >= 20) return 'Estado funcional deficiente - Requiere atención prioritaria';
  return 'Estado funcional muy comprometido - Evaluación especializada recomendada';
};

const generateRecommendations = (domainScores) => {
  const recommendations = [];
  
  Object.entries(domainScores).forEach(([domain, data]) => {
    if (data.score < 60) {
      switch (domain) {
        case 'equilibrio':
          recommendations.push('Ejercicios de equilibrio estático y dinámico');
          break;
        case 'coordinacion':
          recommendations.push('Ejercicios de coordinación mano-ojo y motricidad fina');
          break;
        case 'fuerza':
          recommendations.push('Programa de fortalecimiento muscular progresivo');
          break;
        case 'flexibilidad':
          recommendations.push('Estiramientos y ejercicios de movilidad articular');
          break;
        case 'propiocepcion':
          recommendations.push('Entrenamiento propioceptivo en superficies inestables');
          break;
      }
    }
  });

  return recommendations;
};

// Exportar todos los schemas adicionales
export const ADDITIONAL_QUESTIONNAIRES = [
  {
    id: 'odi',
    schema: ODI_SCHEMA,
    icon: 'spine',
    color: 'purple',
  },
  {
    id: 'efp0',
    schema: EFP0_SCHEMA,
    icon: 'activity',
    color: 'emerald',
  },
];
